import { useDashboardToAddToFromParam } from '@modules/analysis/hooks';
import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  useGetArtefactScope,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import {
  getDashboardLink,
  getDashboardRoute,
  getShortMetadataForDashboard,
} from '@modules/dashboard/helpers';
import { DashboardHeader } from '@modules/dashboard/types';
import { useSearchAnalysesWhereClause } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { getSearchLink } from '@modules/feed/utils/getSearchLink';
import { getSearchRoute } from '@modules/feed/utils/getSearchRoute';

import {
  AnalysisUseCase,
  CreateDashboardInput,
  ListDashboardsDocument,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useGetUser } from 'v2/core/services/user';
import { getSearchMetadataUrl } from '../utils';

export const trackSearchDashboardEvents =
  createAnalyticsTracker('searchDashboard');

export const useSaveFeedAnalysis = () => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const {
    id: userID,
    tenant: { id: tenantID, slug: tenantSlug },
  } = useGetUser();

  const navigate = useNavigate();
  const searchQuery = useFeedStore(({ query }) => query);
  const { showErrorNotification } = useNotificationContext();
  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const prompt = useFeedStore((state) => state.prompt);

  const searchWhereClause = useSearchAnalysesWhereClause();

  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause: searchWhereClause,
    dateRange: searchQuery.duration as EQLProps['dateRange'],
  };

  const {
    dashboardToAddToFromParam,
    rowID: dashboardLayoutRowID,
    cellInsertDirection,
  } = useDashboardToAddToFromParam();

  const { createAnalysis, isLoading: isCreatingAnalysis } = useCreateAnalysis({
    useCase: AnalysisUseCase.Feed,
    name: '',
    scope: Scope.HIDDEN,
    eqlProps,
    metadata: '',
    dashboardLayoutRowID,
    cellInsertDirection,

    dashboardToAddTo: dashboardToAddToFromParam?.id
      ? dashboardToAddToFromParam
      : undefined,
  });

  const { eql: analysisEql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const isLoading = isCreatingAnalysis;

  const artefactScope = useGetArtefactScope();

  useFullscreenLoader({ isLoading });

  const onSaveSearchDashboard = useCallback(
    async (
      name: string,
      scope: Scope,
      shouldRedirect = true,
      newDashboard?: DashboardHeader,
    ) => {
      try {
        const metadata = getSearchMetadataUrl({
          tenantSlug,
          ...searchQuery!,
          isSearchToFeedEnabled,
        });

        const shortMetadata = getShortMetadataForDashboard({
          dashboardEmoji: newDashboard?.emoji,
        });

        const addToNewDashboard: CreateDashboardInput | undefined =
          newDashboard?.name
            ? {
                tenantID,
                title: newDashboard.name,
                createdBy: userID,
                scope: artefactScope,
                metadata: '',
                shortMetadata,
              }
            : undefined;

        const savedAnalysis: any = await createAnalysis({
          variables: {
            tenantID,
            title: name,
            scope: mapToScope(scope),
            panels: [],
            metadata,
            analysisExpresssion: analysisEql ?? '',
            prompt,
            createdBy: userID,
            useCase: AnalysisUseCase.Feed,
            addToNewDashboard,
            dashboardToAddTo: dashboardToAddToFromParam?.id
              ? {
                  id: dashboardToAddToFromParam.id,
                  editToken: dashboardToAddToFromParam.editToken,
                }
              : undefined,
          },
          refetchQueries: addToNewDashboard
            ? [ListDashboardsDocument]
            : undefined,
        });

        const savedAnalysisData = savedAnalysis?.data.createAnalysis;

        if (savedAnalysisData.viewToken) {
          const artefactRoute = dashboardToAddToFromParam?.id
            ? getDashboardRoute(dashboardToAddToFromParam)
            : getSearchRoute({
                name,
                token: savedAnalysisData.viewToken,
                isSearchToFeedEnabled,
              });
          if (shouldRedirect) {
            navigate(artefactRoute);
          }
          dispatch({
            type: SearchActionType.SET_VISUAL,
            payload: {
              hasChangesToBeSaved: false,
            },
          });
          const artefactLink = dashboardToAddToFromParam?.id
            ? getDashboardLink(dashboardToAddToFromParam, tenantSlug)
            : getSearchLink({
                name,
                token: savedAnalysisData.viewToken,
                tenantSlug,
                isSearchToFeedEnabled,
              });
          trackSearchDashboardEvents({
            eventName: 'Search Dashboard Saved',
            artefactLink,
            name,
            scope,
          });
          return { artefactLink, artefactRoute };
        }
      } catch {
        showErrorNotification({
          contentComponent: `Failed to create new ${
            isSearchToFeedEnabled ? 'Feed' : 'Search'
          }`,
          props: { isClosable: true },
        });
      }
      return { artefactLink: null, artefactRoute: null };
    },
    [
      tenantSlug,
      searchQuery,
      isSearchToFeedEnabled,
      tenantID,
      userID,
      artefactScope,
      createAnalysis,
      analysisEql,
      prompt,
      dashboardToAddToFromParam,
      dispatch,
      navigate,
      showErrorNotification,
    ],
  );

  return { onSaveSearchDashboard };
};
